import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Train, TreeDeciduous, Store, Eye } from 'lucide-react';

const LocationSection = () => {
  const { t } = useLanguage();

  const locationFeatures = [
    { icon: Train, title: t('location_paris'), desc: t('location_paris_desc') },
    { icon: TreeDeciduous, title: t('location_calm'), desc: t('location_calm_desc') },
    { icon: Store, title: t('location_amenities'), desc: t('location_amenities_desc') },
    { icon: Eye, title: t('location_nature'), desc: t('location_nature_desc') },
  ];

  return (
    <section 
      className="py-24 md:py-32 bg-[#F7F7F5]"
      data-testid="location-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] font-['Playfair_Display'] mb-4"
              data-testid="location-title"
            >
              {t('location_title')}
            </h2>
            <p className="text-[#5C5C59] text-lg mb-10 font-['Playfair_Display'] italic">
              {t('location_subtitle')}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {locationFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={index} 
                    className="flex items-start gap-4"
                    data-testid={`location-feature-${index}`}
                  >
                    <div className="flex-shrink-0 w-11 h-11 bg-[#1A2F23] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-[#1A1A1A] mb-1 font-['Manrope']">
                        {feature.title}
                      </h3>
                      <p className="text-[#5C5C59] text-sm">
                        {feature.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Right - Google Map */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-square overflow-hidden shadow-lg" data-testid="location-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d42000!2d2.12!3d48.88!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e665c4b6b5c7e9%3A0x40b82c3688c5790!2sCroissy-sur-Seine!5e0!3m2!1sfr!2sfr!4v1710000000000!5m2!1sfr!2sfr"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(30%) contrast(1.1)' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localisation villa de luxe Croissy-sur-Seine - 30 min de Paris par RER"
              />
            </div>

            {/* Decorative corner accent */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-[#C5A059] -z-10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LocationSection;
